import { buildHiddenContextBlock, createOptimisticUserMessage, findLastAssistantMessage, toChatError } from './utils/chatHelpers';
import { Metrics } from './chatMetrics';
import { isArborHost } from './isArborHost';
import { ToolUseChirpContentBlockTypename, ServerToolUseChirpContentBlockTypename, ContextChirpContentBlockTypename, TextChirpContentBlockTypename } from '../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { matchesNegativeKeyword } from './utils/matchesNegativeKeyword';
import { getConversationRpc } from '../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineConversationsService';
import { createToolResolver } from './coordinator/toolResolver';
import { createConversationLoader } from './coordinator/conversationLoader';
import { mergeSkillsContexts } from './utils/mergeSkillsContexts';
import { createConversationHistory } from './conversationHistory';
import { createDefaultChirpClient } from './chirpClient';
import { createStreamingBreezeConnection } from './streaming/streamingConnection';
import { createPollingBreezeConnection } from './polling/pollingConnection';
import { createAblyBreezeConnection } from './ably/ablyConnection';
import { createStreamingTransport } from './streaming/streamingTransport';
import { createPollingTransport } from './polling/pollingTransport';
import { createAblyTransport } from './ably/ablyTransport';
import { chatReducer } from './reducer/chatReducer';
import { getInitialState } from './reducer/types';
import { breezeEngineRegistry } from '../sdk/BreezeEngineRegistry';
import { createChatLogger } from './logger';
import { createChatTelemetry, noopTelemetry } from './chatTelemetry';
// Cap queued messages to avoid unbounded buffering while the client is busy.
// 3 strikes a balance between "user can type ahead" and "don't hide a wall of
// unsent text that fires off unexpectedly when the turn completes."
const MAX_QUEUE_DEPTH = 3;
const TITLE_POLL_INTERVAL_MS = 2000;
const TITLE_POLL_MAX_ATTEMPTS = 15;

// Bounded pre-banner recovery: how many times a transient send error is retried
// (by reloading/resuming the conversation) before the error banner is shown,
// and the base backoff between attempts (grows 300 / 600 / 1200 ms).
const MAX_RECOVERY_ATTEMPTS = 3;
const RECOVERY_BASE_DELAY_MS = 300;
/**
 * A turn is steerable whenever it is in flight -- streaming, or paused
 * mid-turn awaiting a tool result or an approval. In all three the server has a
 * live turn to attach the message to and dispositions it accordingly
 * (STEERED_EXISTING_TURN or QUEUED_FOLLOW_UP). AWAITING_TOOL / AWAITING_APPROVAL
 * are deliberately included: they are common and often long (any tool call), so
 * the old STREAMING-only gate silently dropped every steer sent during a tool
 * pause -- the lost-steering-message bug. READY/LOADING/CANCELLING/ERROR have no
 * live turn to steer.
 */
export function isSteerableStatus(status) {
  return status === 'STREAMING' || status === 'AWAITING_TOOL' || status === 'AWAITING_APPROVAL';
}
export function createChatClient(options) {
  var _options$__onInit, _transport$setClientT;
  const {
    transport,
    agentId: initialAgentId,
    conversationHistory,
    conversationsChirpClient,
    pollConversationTitle = true,
    onError: onErrorCallback,
    onConversationCreated: onConversationCreatedCallback,
    onStatusChange: onStatusChangeCallback,
    onStreamEvent: onStreamEventCallback,
    onTitleChanged: onTitleChangedCallback,
    onBeforeTurnComplete: onBeforeTurnCompleteCallback,
    onAfterTurnComplete: onAfterTurnCompleteCallback,
    registry = breezeEngineRegistry,
    logger = createChatLogger(),
    telemetry = noopTelemetry
  } = options;
  const metricsEnabled = !isArborHost();
  let state = getInitialState({
    defaultAgentId: initialAgentId,
    agentId: initialAgentId
  });
  let toolMap = new Map();
  let cachedToolSpecs = [];
  let cachedSkillSpecs = [];
  let cachedSubagentSpecs = [];
  let cachedArtifactPluginSpecs = [];
  let cachedSnapshot = null;
  // Per-send `messageContexts` from the turn that is currently in flight. The
  // FE only attaches these on the initiating `sendMessage` (e.g. a journey's
  // SkillsContext), but a turn paused by `ask_user_question` resumes via
  // `sendToolResponse`, which has no per-call sendOptions. Without replaying
  // them the backend rebuilds the agent with no SkillsContext and silently
  // drops registry skills + their tools. Captured here so the resume path can
  // re-send them for the same turnId.
  let pendingSendMessageContexts = [];
  // Per-call `turnMetadata` (e.g. Teamwork's approval/plan mode) captured so the
  // resume path can replay it for the same turnId -- resumes carry no per-call
  // sendOptions, and dropping it lets the backend rebuild the agent with no
  // approval policy. Mirrors `pendingSendMessageContexts`.
  let pendingSendTurnMetadata = {};
  let dirty = true;
  let drainingQueue = false;
  // A send flips status to STREAMING before onTurnStarted delivers the live
  // ids. Steers typed in that gap are buffered here and flushed once the ids
  // land, rather than dropped as `missing-ids`.
  let awaitingTurnStart = false;
  // Each entry keeps its caller's promise settlers so a buffered steer's return
  // value reflects the real outcome: the transport response once flushed, or
  // undefined if the turn ends first -- never a premature "dropped" undefined.
  let pendingSteers = [];
  // Bounded pre-banner recovery state. `recoveryAttempts` is reset at the start
  // of each new user send; `isRecovering` guards against re-entrancy and, while
  // set, downgrades a would-be ERROR to a soft error so the banner never
  // flashes between attempts.
  let recoveryAttempts = 0;
  let isRecovering = false;
  const listeners = new Set();
  let titlePollGeneration = 0;
  async function pollForTitle(conversationId) {
    if (!conversationsChirpClient || !pollConversationTitle) return;
    const generation = ++titlePollGeneration;
    let attempts = 0;
    while (attempts < TITLE_POLL_MAX_ATTEMPTS) {
      if (state.title !== null || state.error !== null || titlePollGeneration !== generation) return;
      await new Promise(resolve => setTimeout(resolve, TITLE_POLL_INTERVAL_MS));
      if (state.title !== null || state.error !== null || titlePollGeneration !== generation) return;
      try {
        const response = await conversationsChirpClient.call(getConversationRpc, {
          request: {
            conversationId
          }
        });
        // re-check after the async call; generation may have changed while in-flight
        if (titlePollGeneration !== generation) return;
        if (response.title) {
          dispatch({
            type: 'TITLE_UPDATED',
            title: response.title
          });
          onTitleChangedCallback === null || onTitleChangedCallback === void 0 || onTitleChangedCallback(conversationId, response.title);
          return;
        }
      } catch (_unused) {
        // ignore, retry on next interval
      }
      attempts++;
    }
  }
  function dispatch(incomingAction) {
    // While a bounded recovery is in flight, a failed reload/resume would flip
    // the status to ERROR and flash the banner between attempts. Downgrade it
    // to a soft error (records the error, keeps the loading status) so recovery
    // is invisible; `recoverOrSurface` reads `state.error` to detect a failed
    // attempt and surfaces a real ERROR only once attempts are exhausted.
    const action = isRecovering && incomingAction.type === 'TURN_ERROR' ? {
      type: 'TURN_ERROR_SOFT',
      error: incomingAction.error
    } : incomingAction;
    const prev = state;
    state = chatReducer(prev, action);
    if (state === prev) {
      logger.dispatchNoop(action);
      return;
    }
    logger.dispatch(action, prev, state);
    // Edge-trigger on READY→active so the turn-duration timer starts once per
    // turn, even when drainQueue sends back-to-back without returning to READY.
    if (metricsEnabled && action.type === 'STATUS_SET' && prev.status === 'READY') {
      telemetry.turnStarted();
    }
    // Edge-trigger on the transition into READY so redundant TURN_COMPLETED
    // dispatches (e.g. external-tool channel death + final END_TURN) count once.
    if (metricsEnabled && action.type === 'TURN_COMPLETED' && prev.status !== 'READY') {
      telemetry.turnCompleted();
    }
    dirty = true;
    notify();
    if (prev.status !== state.status) {
      onStatusChangeCallback === null || onStatusChangeCallback === void 0 || onStatusChangeCallback(state.status, prev.status);
    }
    // Any READY/ERROR boundary ends the turn a buffered steer was waiting on --
    // resolve those steers as undelivered so none can leak into the next turn.
    // Covers a recovery that reaches READY without ever firing onTurnStarted
    // (the resume path dispatches TURN_STARTED directly, so flush never runs).
    if (prev.status !== state.status && (state.status === 'READY' || state.status === 'ERROR')) {
      awaitingTurnStart = false;
      if (pendingSteers.length > 0) {
        const dropped = pendingSteers;
        pendingSteers = [];
        dropped.forEach(({
          resolve
        }) => {
          telemetry.steerDropped({
            reason: 'turn-ended-before-start',
            status: prev.status
          });
          resolve(undefined);
        });
      }
    }
    if (prev.conversationId === null && state.conversationId !== null) {
      onConversationCreatedCallback === null || onConversationCreatedCallback === void 0 || onConversationCreatedCallback(state.conversationId);
    }
    if (state.status === 'ERROR' && prev.status !== 'ERROR' && state.error) {
      telemetry.turnError(state.error);
      onErrorCallback === null || onErrorCallback === void 0 || onErrorCallback(state.error);
    }
    // ERROR_RECOVERED is a connection-level recovery, not a turn boundary, so it
    // must not send anything. The queue is left intact and drains on the next
    // real turn completion (or explicit send).
    if (prev.status !== 'READY' && state.status === 'READY' && action.type !== 'ERROR_RECOVERED') {
      // Skip while recovering: loadConversation's RESET briefly flips status to
      // READY mid-recovery, and draining here would start a fresh turn racing
      // the in-flight reload (and reset recoveryAttempts). The queue is left
      // intact; recoverOrSurface drains it once recovery releases the turn.
      if (state.messageQueue.length > 0 && !drainingQueue && !isRecovering) {
        void drainQueue();
      }
    }
  }
  (_options$__onInit = options.__onInit) === null || _options$__onInit === void 0 || _options$__onInit.call(options, {
    dispatch
  });
  function rebuildToolSpecs() {
    cachedToolSpecs = [...toolMap.values()].map(t => ({
      name: t.name,
      description: t.description,
      inputSchema: JSON.stringify(t.inputSchema)
    }));
  }
  if (options.tools) {
    toolMap = new Map(options.tools.map(t => [t.name, t]));
    rebuildToolSpecs();
  }
  logger.recordingStart({
    agentId: initialAgentId,
    conversationId: null,
    messages: [],
    messageTurnIds: [],
    tools: [...toolMap.values()].map(t => ({
      name: t.name,
      description: t.description,
      inputSchema: t.inputSchema
    })),
    transportMode: options.transportMode === 'ably' || options.transportMode === 'streaming' ? options.transportMode : undefined
  });
  function getMergedToolSpecs() {
    const registrySpecs = registry.getExternalToolSpecs();
    const localNames = new Set(cachedToolSpecs.map(spec => spec.name));
    const filtered = registrySpecs.filter(spec => !localNames.has(spec.name));
    return [...cachedToolSpecs, ...filtered];
  }
  function getMergedSkillSpecs() {
    const registrySpecs = registry.getRegistrySkillSpecs();
    const localNames = new Set(cachedSkillSpecs.map(spec => spec.name));
    const filtered = registrySpecs.filter(spec => !localNames.has(spec.name));
    return [...cachedSkillSpecs, ...filtered];
  }
  function resolveMetadataTarget() {
    return state.conversationId != null ? 'turnMetadata' : 'conversationMetadata';
  }
  function getRegistryConversationMetadata() {
    const items = registry.getConversationMetadata();
    if (items.length === 0) return {};
    const metadata = {};
    for (const item of items) {
      if (item.value === null || item.value === undefined) continue;
      metadata[item.contextKey] = typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value);
    }
    return metadata;
  }
  function buildSendMetadata(sendOptions) {
    const target = resolveMetadataTarget();
    const registryMeta = getRegistryConversationMetadata();
    let turn;
    let conversation;
    if (target === 'turnMetadata') {
      turn = Object.assign({}, registryMeta, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.turnMetadata);
      conversation = Object.assign({}, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.conversationMetadata);
    } else {
      turn = Object.assign({}, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.turnMetadata);
      conversation = Object.assign({}, registryMeta, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.conversationMetadata);
    }
    return Object.assign({}, Object.keys(turn).length > 0 && {
      turnMetadata: turn
    }, Object.keys(conversation).length > 0 && {
      conversationMetadata: conversation
    });
  }
  function buildSendContexts(sendOptions) {
    var _sendOptions$messageC, _sendOptions$fileRefe;
    const messageContexts = mergeSkillsContexts([...registry.getMessageContexts(), ...((_sendOptions$messageC = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.messageContexts) !== null && _sendOptions$messageC !== void 0 ? _sendOptions$messageC : [])]);
    return Object.assign({}, messageContexts.length > 0 && {
      messageContexts
    }, (sendOptions === null || sendOptions === void 0 || (_sendOptions$fileRefe = sendOptions.fileReferences) === null || _sendOptions$fileRefe === void 0 ? void 0 : _sendOptions$fileRefe.length) && {
      fileReferences: sendOptions.fileReferences
    });
  }

  // Build everything in the transport request except the message payload itself
  // -- agent identity, conversation tracking, metadata, tools/skills, contexts,
  // and file references. Used by both the regular send path and the queue
  // drain path; the caller adds the `message` field (a string or a content-
  // block array) on top.
  function buildBaseSendRequest(sendOptions) {
    return Object.assign({
      agentId: state.agentId
    }, state.conversationId ? {
      conversationId: state.conversationId
    } : {}, sendOptions !== null && sendOptions !== void 0 && sendOptions.collectionId ? {
      collectionId: sendOptions.collectionId
    } : {}, (sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.configVersion) != null ? {
      configVersion: sendOptions.configVersion
    } : {}, buildSendMetadata(sendOptions), {
      externalToolSpecs: getMergedToolSpecs(),
      externalSkillSpecs: getMergedSkillSpecs(),
      externalSubagentSpecs: cachedSubagentSpecs.length > 0 ? cachedSubagentSpecs : undefined,
      externalArtifactPluginSpecs: cachedArtifactPluginSpecs.length > 0 ? cachedArtifactPluginSpecs : undefined
    }, buildSendContexts(sendOptions));
  }

  // Merge per-call `sendOptions.context` with the registry's running context.
  // The string-message path prepends the result as a hidden `<context>` block
  // so the agent can see it without it appearing in the chat UI.
  function getEffectiveContext(sendOptions) {
    const registryCtx = registry.getContext();
    const callerCtx = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.context;
    if (callerCtx === undefined) return registryCtx;
    return Object.assign({}, registryCtx, callerCtx);
  }
  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }
  function resetPendingSend() {
    pendingSendMessageContexts = [];
    pendingSendTurnMetadata = {};
  }
  const clientCtx = {
    getState: () => state,
    dispatch,
    transport,
    logger,
    telemetry,
    createHandler,
    getMergedToolSpecs,
    getExternalSkillSpecs: () => getMergedSkillSpecs(),
    getExternalSubagentSpecs: () => cachedSubagentSpecs,
    getExternalArtifactPluginSpecs: () => cachedArtifactPluginSpecs,
    // On resume there are no per-call sendOptions, so this replays the current
    // registry context. conversationId is set, so buildSendMetadata routes
    // registry metadata to turnMetadata; conversationMetadata/fileReferences are
    // dropped since the backend persists/re-fetches those on resume.
    getResumeMetadata: () => {
      const {
        turnMetadata
      } = buildSendMetadata(undefined);
      const merged = Object.assign({}, turnMetadata, pendingSendTurnMetadata);
      return Object.keys(merged).length > 0 ? {
        turnMetadata: merged
      } : {};
    },
    getResumeContexts: () => {
      const {
        messageContexts: registryContexts
      } = buildSendContexts(undefined);
      const messageContexts = mergeSkillsContexts([...(registryContexts !== null && registryContexts !== void 0 ? registryContexts : []), ...pendingSendMessageContexts]);
      return messageContexts.length > 0 ? {
        messageContexts
      } : {};
    },
    resetPendingSend,
    onBeforeTurnComplete: onBeforeTurnCompleteCallback,
    onAfterTurnComplete: onAfterTurnCompleteCallback
  };
  const toolResolver = createToolResolver(clientCtx, () => toolMap, registry);
  // The one client-vs-server tool predicate, shared by the reload loader and
  // the live-stream stall watchdog. Reads the live toolMap (reassigned by
  // setTools) so both paths track runtime tool changes identically.
  const isClientTool = toolName => toolMap.has(toolName) || registry.hasTool(toolName);
  (_transport$setClientT = transport.setClientTool) === null || _transport$setClientT === void 0 || _transport$setClientT.call(transport, isClientTool);
  const conversationLoader = createConversationLoader(clientCtx, conversationHistory, {
    onError: onErrorCallback,
    onPendingToolsFound: () => toolResolver.handleExternalTool(),
    onPendingApprovalFound: () => toolResolver.handleToolApproval(),
    isClientTool
  });
  function createHandler() {
    return {
      onStreamEvent(event) {
        onStreamEventCallback === null || onStreamEventCallback === void 0 || onStreamEventCallback(event);
      },
      onTurnStarted(cId, tId, title) {
        // isFirstTurn covers two cases:
        // 1. Brand new conversation (conversationId was null)
        // 2. First real message in a loaded conversation (loadConversation sets
        //    turnId=0 as a sentinel; the real first turn has a non-zero turnId)
        const isFirstTurn = state.conversationId === null || state.turnId === 0;
        dispatch({
          type: 'TURN_STARTED',
          conversationId: cId,
          turnId: tId,
          title
        });
        // Live ids are in `state` now -- release the gap and replay buffered steers.
        awaitingTurnStart = false;
        void flushPendingSteers();
        if (title !== undefined) {
          onTitleChangedCallback === null || onTitleChangedCallback === void 0 || onTitleChangedCallback(cId, title);
        }
        if (isFirstTurn && state.title === null) {
          void pollForTitle(state.conversationId);
        }
      },
      onMessageUpdate(update) {
        switch (update.type) {
          case 'append':
            {
              // Skip metric emission when the reducer would dedupe this append
              // (resume/recovery streams replay history before delivering new
              // events). Counting here would double-count tool-invoked even
              // though the duplicate MESSAGE_APPEND is a no-op in state.
              const isNewMessage = !state.messages.some(existing => existing.id === update.message.id);
              if (isNewMessage && Array.isArray(update.message.content)) {
                for (const block of update.message.content) {
                  if (metricsEnabled && (block.__typename === ToolUseChirpContentBlockTypename || block.__typename === ServerToolUseChirpContentBlockTypename)) {
                    Metrics.counter('tool-invoked', {
                      tool_name: block.name,
                      agent_id: state.agentId
                    }).increment();
                  }
                }
              }
              dispatch({
                type: 'MESSAGE_APPEND',
                message: update.message,
                keepDeltas: update.stopReason === 'CANCELLED'
              });
              break;
            }
          case 'delta-update':
            if (metricsEnabled) {
              telemetry.tokenReceived();
            }
            dispatch({
              type: 'MESSAGE_DELTA_UPDATE',
              message: update.message
            });
            break;
          case 'replace-optimistic':
            dispatch({
              type: 'MESSAGE_REPLACE_OPTIMISTIC',
              message: update.message
            });
            break;
          case 'replace-all':
            dispatch({
              type: 'MESSAGE_REPLACE_ALL',
              messages: update.messages
            });
            break;
          case 'reconcile-all':
            dispatch({
              type: 'MESSAGE_RECONCILE_ALL',
              messages: update.messages
            });
            break;
          default:
            return;
        }
      },
      async onStopReason(stopReason) {
        if (metricsEnabled && stopReason === 'CANCELLED') {
          telemetry.turnCancelled();
        }
        await toolResolver.handleStopReason(stopReason);
      },
      async onComplete() {
        var _findLastAssistantMes;
        if (onBeforeTurnCompleteCallback) {
          try {
            await onBeforeTurnCompleteCallback({
              conversationId: state.conversationId,
              turnId: state.turnId
            });
          } catch (_unused2) {
            // A failed prefetch must not strand the conversation in STREAMING.
          }
        }
        dispatch({
          type: 'TURN_COMPLETED'
        });
        // Mirror completeTurn(): the transports complete a turn via either
        // onStopReason (-> toolResolver.completeTurn) or onComplete, never both,
        // so onAfterTurnComplete must fire here too (e.g. the polling transport's
        // COMPLETED/CANCELLED path and the streaming no-stop-reason fallback).
        onAfterTurnCompleteCallback === null || onAfterTurnCompleteCallback === void 0 || onAfterTurnCompleteCallback({
          conversationId: state.conversationId,
          turnId: state.turnId,
          stopReason: (_findLastAssistantMes = findLastAssistantMessage(state.messages)) === null || _findLastAssistantMes === void 0 ? void 0 : _findLastAssistantMes.stopReason
        });
      },
      onError(err) {
        dispatch({
          type: 'MESSAGE_REMOVE_LAST_OPTIMISTIC'
        });
        // Fire-and-forget is safe here: recoverOrSurface has an internal
        // try/catch/finally and never rejects (it surfaces the error itself on
        // any failure), so this cannot produce an unhandled rejection. The
        // .catch is a belt-and-suspenders guard in case that ever changes.
        recoverOrSurface(toChatError(err)).catch(() => {
          dispatch({
            type: 'TURN_ERROR',
            error: toChatError(err)
          });
        });
      },
      onReconnecting() {
        dispatch({
          type: 'RECONNECTING_SET'
        });
      }
    };
  }

  // Only transient transport failures are worth retrying. Typed backend errors
  // (auth, agent-not-found, AI-settings, etc.) are not transient and must
  // surface immediately.
  function isRecoverableError(error) {
    return error.type === 'TRANSPORT_ERROR' || error.type === 'UNKNOWN';
  }

  // A transient Ably drop usually resolves if we reconnect and resume the turn,
  // or reload the conversation from the store -- which is exactly what
  // `loadConversation` does. So on such an error we retry that up to
  // `MAX_RECOVERY_ATTEMPTS` (with backoff) before surfacing the error banner.
  // Requires a `conversationId` to reload against; a brand-new send that died
  // before its turn started has nothing to reload, so it surfaces immediately
  // (re-POSTing could duplicate the turn). `recoveryAttempts` is reset per new
  // user send; `isRecovering` prevents a failed reload from recursing into
  // another recovery.
  // Recovery is scoped to Ably (Breeze Assistant's transport, where transient
  // drops dominate). Streaming already retries inside its own transport, and
  // polling has no equivalent transient-drop class, so both keep surfacing
  // errors as before.
  const recoveryEnabled = options.transportMode === 'ably';
  async function recoverOrSurface(error) {
    const conversationId = state.conversationId;
    if (!recoveryEnabled || isRecovering || conversationId === null || !isRecoverableError(error) || recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      dispatch({
        type: 'TURN_ERROR',
        error: toChatError(error)
      });
      return;
    }
    isRecovering = true;
    try {
      while (recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
        recoveryAttempts++;
        telemetry.turnRecoveryAttempt(error);
        const backoffMs = RECOVERY_BASE_DELAY_MS * 2 ** (recoveryAttempts - 1);
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          setTimeout(resolve, backoffMs);
        });
        // Abandon recovery if the user switched away from (or cleared) the
        // conversation we started recovering while we were waiting.
        if (state.conversationId !== conversationId) return;
        // eslint-disable-next-line no-await-in-loop
        await conversationLoader.loadConversation(conversationId);
        // loadConversation clears `error` on entry (RESET) and only sets it
        // (downgraded to soft above) when the reload/resume itself fails, so a
        // null error means the conversation is usable again -- READY or a
        // restored client-tool pause.
        if (state.error === null) {
          telemetry.turnRecovered(error);
          return;
        }
      }
      telemetry.turnRecoveryExhausted(error);
    } catch (_unused3) {
      // Defensive: loadConversation resolves today (it handles its own failures
      // via TURN_ERROR_SOFT). If a future change makes it throw, never leave the
      // turn hung on a swallowed rejection -- fall through and surface the error.
      // This is also why the two callers can safely fire-and-forget.
    } finally {
      isRecovering = false;
    }
    // Exhausted (or an unexpected throw): surface the original error.
    // `isRecovering` is now false, so this hits the ERROR transition and fires
    // turnError/onError exactly once.
    dispatch({
      type: 'TURN_ERROR',
      error: toChatError(error)
    });
  }
  async function drainQueue() {
    if (state.messageQueue.length === 0 || drainingQueue) return;
    drainingQueue = true;
    recoveryAttempts = 0;
    const next = state.messageQueue[0];
    dispatch({
      type: 'QUEUE_DEQUEUE'
    });
    const registryCtx = registry.getContext();
    const effectiveContent = Object.keys(registryCtx).length > 0 ? `${buildHiddenContextBlock(registryCtx)}${next.content}` : next.content;
    const optimisticMsg = createOptimisticUserMessage(effectiveContent);
    dispatch({
      type: 'MESSAGE_APPEND',
      message: optimisticMsg
    });
    dispatch({
      type: 'STATUS_SET',
      status: transport.activeStatus
    });
    awaitingTurnStart = true;
    resetPendingSend();
    drainingQueue = false;
    try {
      await transport.send(Object.assign({
        agentId: state.agentId
      }, state.conversationId ? {
        conversationId: state.conversationId
      } : {}, {
        message: effectiveContent
      }, buildSendMetadata(undefined), {
        externalToolSpecs: getMergedToolSpecs(),
        externalSkillSpecs: getMergedSkillSpecs(),
        externalSubagentSpecs: cachedSubagentSpecs.length > 0 ? cachedSubagentSpecs : undefined,
        externalArtifactPluginSpecs: cachedArtifactPluginSpecs.length > 0 ? cachedArtifactPluginSpecs : undefined
      }, buildSendContexts(undefined)), createHandler());
    } catch (_unused4) {
      dispatch({
        type: 'QUEUE_RESTORE_FAILED',
        item: next
      });
      dispatch({
        type: 'MESSAGE_REMOVE_LAST_OPTIMISTIC'
      });
    }
  }

  // Shared tail for both `sendMessage` and `sendMessages` once gating is done:
  // record the message, set status to streaming, build the send request, and
  // hand off to the transport. `optimisticContent` is what the user sees in
  // their bubble; `wireContent` is what goes on the wire (may include the
  // hidden-context prefix that the string path adds).
  async function executeSend(optimisticContent, wireContent, sendOptions) {
    var _sendOptions$messageC2, _sendOptions$turnMeta;
    recoveryAttempts = 0;
    if (metricsEnabled && !state.conversationId) {
      Metrics.counter('chat-session-started', {
        agent_id: state.agentId
      }).increment();
    }
    if (!(sendOptions !== null && sendOptions !== void 0 && sendOptions.hidden)) {
      const messageText = typeof optimisticContent === 'string' ? optimisticContent : optimisticContent.filter(block => block.__typename === TextChirpContentBlockTypename).map(block => 'text' in block ? block.text : '').join(' ');
      if (metricsEnabled) {
        Metrics.counter('message-sent', {
          agent_id: state.agentId,
          has_negative_keyword: String(matchesNegativeKeyword(messageText))
        }).increment();
      }
      dispatch({
        type: 'MESSAGE_APPEND',
        message: createOptimisticUserMessage(optimisticContent, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.fileReferences)
      });
    }
    dispatch({
      type: 'STATUS_SET',
      status: transport.activeStatus
    });
    awaitingTurnStart = true;
    if (state.error) {
      dispatch({
        type: 'CLEAR_ERROR'
      });
    }
    pendingSendMessageContexts = (_sendOptions$messageC2 = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.messageContexts) !== null && _sendOptions$messageC2 !== void 0 ? _sendOptions$messageC2 : [];
    pendingSendTurnMetadata = (_sendOptions$turnMeta = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.turnMetadata) !== null && _sendOptions$turnMeta !== void 0 ? _sendOptions$turnMeta : {};
    const sendRequest = Object.assign({}, buildBaseSendRequest(sendOptions), {
      message: wireContent
    }, (sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.hidden) && {
      skipUserEcho: true
    });
    logger.transportSend(sendRequest);
    try {
      await transport.send(sendRequest, createHandler());
    } catch (error) {
      if (!(sendOptions !== null && sendOptions !== void 0 && sendOptions.hidden)) {
        dispatch({
          type: 'MESSAGE_REMOVE_LAST_OPTIMISTIC'
        });
      }
      await recoverOrSurface(toChatError(error));
    }
  }
  function clear() {
    titlePollGeneration++;
    resetPendingSend();
    awaitingTurnStart = false;
    pendingSteers.forEach(({
      resolve
    }) => resolve(undefined));
    pendingSteers = [];
    transport.clear();
    dispatch({
      type: 'RESET'
    });
  }
  async function cancelActiveTurn(cId, tId, reason = 'User cancelled') {
    if (cId !== null && tId !== null && transport.isPersistentChannel) {
      // Persistent-channel (Ably): stay subscribed and keep the input locked
      // until the interrupt (stopReason: CANCELLED) arrives and drives
      // TURN_COMPLETED, so the user can't send before it's shown.
      dispatch({
        type: 'STATUS_SET',
        status: 'CANCELLING'
      });
      await transport.cancelTurn({
        conversationId: cId,
        turnId: tId,
        reason
      });
      return;
    }
    // Non-persistent (streaming/polling): tear down locally and go READY now.
    transport.stop();
    dispatch({
      type: 'STATUS_SET',
      status: 'READY'
    });
    if (cId !== null && tId !== null) {
      await transport.cancelTurn({
        conversationId: cId,
        turnId: tId,
        reason
      });
    }
  }

  // The user-facing entry: counts one attempt (the denominator for the steered
  // / dropped counters) and hands off to `deliverSteer`. Buffer replays call
  // `deliverSteer` directly so a buffered steer counts as a single attempt.
  function steerTurn(message) {
    telemetry.steerAttempted();
    return deliverSteer(message);
  }
  async function deliverSteer(message) {
    const statusAtSend = state.status;
    if (!isSteerableStatus(statusAtSend)) {
      telemetry.steerDropped({
        reason: 'not-steerable',
        status: statusAtSend
      });
      return undefined;
    }
    // In flight but ids not live yet -- buffer, don't drop. The returned promise
    // settles when the steer is flushed on onTurnStarted (or the turn ends).
    if (awaitingTurnStart) {
      return new Promise((resolve, reject) => {
        pendingSteers.push({
          message,
          resolve,
          reject
        });
      });
    }
    const cId = state.conversationId;
    const tId = state.turnId;
    if (cId === null || tId === null) {
      telemetry.steerDropped({
        reason: 'missing-ids',
        status: statusAtSend
      });
      return undefined;
    }
    const response = await transport.steerTurn({
      conversationId: cId,
      turnId: tId,
      message
    });
    // `transport.steerTurn` returns `SteerTurnResponse | undefined`, and a
    // SteerTurnResponse is always a truthy object, so `undefined` is the only
    // falsy value -- the transport swallowed a failure (e.g. the RPC
    // rejected) and the steer never landed. Check it explicitly to mirror the
    // two dropped-before-send guards above.
    if (response === undefined) {
      telemetry.steerDropped({
        reason: 'transport-undefined',
        status: statusAtSend
      });
      return undefined;
    }
    telemetry.turnSteered(response.disposition);
    return response;
  }

  // Replays buffered steers once the live ids are in `state`, draining serially
  // -- awaiting each before issuing the next -- so the server applies them in
  // the order they were typed rather than in RPC-completion order.
  async function flushPendingSteers() {
    if (pendingSteers.length === 0) return;
    const queued = pendingSteers;
    pendingSteers = [];
    for (const {
      message,
      resolve,
      reject
    } of queued) {
      try {
        // eslint-disable-next-line no-await-in-loop
        resolve(await deliverSteer(message));
      } catch (error) {
        reject(error);
      }
    }
  }
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      if (!dirty && cachedSnapshot) return cachedSnapshot;
      cachedSnapshot = {
        messages: state.messages,
        messageTurnIds: state.messageTurnIdMap,
        messageStableIds: state.messageStableIdMap,
        feedbackByTurnId: state.feedbackByTurnId,
        feedbackByCitationKey: state.feedbackByCitationKey,
        status: state.status,
        conversationId: state.conversationId,
        turnId: state.turnId,
        title: state.title,
        error: state.error,
        activeTools: state.activeTools,
        queuedMessages: state.messageQueue,
        agentId: state.agentId,
        isReconnecting: state.isReconnecting,
        history: {
          hasMore: state.historyHasMore,
          isLoadingMore: state.isLoadingMore
        }
      };
      dirty = false;
      return cachedSnapshot;
    },
    setTools(tools) {
      toolMap = new Map(tools.map(t => [t.name, t]));
      rebuildToolSpecs();
    },
    setExternalSkillSpecs(specs) {
      cachedSkillSpecs = specs;
    },
    setExternalSubagentSpecs(specs) {
      cachedSubagentSpecs = specs;
    },
    setExternalArtifactPluginSpecs(specs) {
      cachedArtifactPluginSpecs = specs;
    },
    async resumeTurn(resumeTurnId, lastMessageId) {
      if (state.conversationId === null || state.status !== 'READY') return;
      recoveryAttempts = 0;
      const conversationId = state.conversationId;
      dispatch({
        type: 'TURN_STARTED',
        conversationId,
        turnId: resumeTurnId
      });
      dispatch({
        type: 'STATUS_SET',
        status: transport.activeStatus
      });
      await transport.resume(Object.assign({
        conversationId,
        turnId: resumeTurnId
      }, lastMessageId != null ? {
        lastMessageId
      } : {}), createHandler());
    },
    async sendMessage(content, sendOptions) {
      var _sendOptions$fileRefe2;
      if (state.status === 'LOADING') return;
      // A bounded recovery is in flight (reloading/resuming the conversation).
      // Treat it like any other busy state and ignore the send rather than
      // queue it: loadConversation's RESET would clear the queue mid-recovery,
      // so a queued message would be silently dropped, and letting it through
      // would start a turn racing the reload. Recovery is short (<= ~2.1s) and
      // ends in READY or a surfaced error, after which sends work normally.
      if (isRecovering) return;
      const isString = typeof content === 'string';
      const isEmpty = isString ? content.trim().length === 0 : content.length === 0;
      // Allow file-only messages — fileReferences are valid content even without text
      if (isEmpty && !(sendOptions !== null && sendOptions !== void 0 && (_sendOptions$fileRefe2 = sendOptions.fileReferences) !== null && _sendOptions$fileRefe2 !== void 0 && _sendOptions$fileRefe2.length)) return;
      const hasActiveTurn = state.status !== 'READY' && state.status !== 'ERROR';
      if (sendOptions !== null && sendOptions !== void 0 && sendOptions.hidden) {
        if (hasActiveTurn) return;
        const wireContent = isString ? [{
          __typename: ContextChirpContentBlockTypename,
          text: content
        }] : content;
        await executeSend(content, wireContent, sendOptions);
        return;
      }
      if (hasActiveTurn) {
        // Active turn in progress (STREAMING / AWAITING_TOOL /
        // AWAITING_APPROVAL / CANCELLING): queue string sends or drop.
        // The reducer's queue stores string content; block-shaped sends are
        // dropped. Widening the queue would require a discriminated-union
        // QueuedMessage + reducer changes.
        if (state.messageQueue.length >= MAX_QUEUE_DEPTH) return;
        if (isString && !isEmpty) {
          dispatch({
            type: 'QUEUE_ENQUEUE',
            content
          });
        }
        return;
      }
      // Hidden-context injection only applies to the string path -- there's no
      // canonical splice point in a block array, so block callers own their
      // content shape entirely.
      let wireContent = content;
      if (isString) {
        const ctx = getEffectiveContext(sendOptions);
        if (Object.keys(ctx).length > 0) {
          wireContent = `${buildHiddenContextBlock(ctx)}${content}`;
        }
      }
      await executeSend(content, wireContent, sendOptions);
    },
    addToolResult(toolUseId, result) {
      const toolCall = state.activeTools.find(t => t.toolUseId === toolUseId);
      if (!toolCall || toolCall.status === 'complete' || toolCall.status === 'error') return;
      const toolResult = {
        content: result.content
      };
      dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: result.isError ? {
          type: 'TOOL_ERROR',
          message: result.content
        } : toolResult,
        status: 'complete'
      });
      toolResolver.checkAndSendToolResponse().catch(err => {
        dispatch({
          type: 'TURN_ERROR',
          error: toChatError(err, 'Failed to send tool response')
        });
      });
    },
    approveTool(toolUseId, {
      approved,
      denialFeedback,
      alwaysAllow
    }) {
      void toolResolver.resolveToolApproval(toolUseId, approved, denialFeedback, alwaysAllow);
    },
    removeQueuedMessage(id) {
      dispatch({
        type: 'QUEUE_REMOVE',
        id
      });
    },
    // Clear a terminal turn error and return the conversation to READY without
    // sending anything. Used by connection-recovery flows (e.g. a stale tab that
    // regained Ably auth) to drop a stale transport error banner once the
    // transport is healthy again. Guarded to ERROR so it can never disturb an
    // in-flight turn, and it never resends the failed message, so no duplicate
    // turn is produced.
    clearError() {
      if (state.status !== 'ERROR') return;
      dispatch({
        type: 'ERROR_RECOVERED'
      });
    },
    async stop(reason) {
      switch (state.status) {
        case 'READY':
        case 'ERROR':
          if (state.messageQueue.length > 0) {
            dispatch({
              type: 'QUEUE_CLEAR'
            });
          }
          return;
        case 'LOADING':
          {
            dispatch({
              type: 'HISTORY_INCREMENT_FETCH_ID'
            });
            dispatch({
              type: 'RESET_TURN'
            });
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            dispatch({
              type: 'STATUS_SET',
              status: 'READY'
            });
            return;
          }
        case 'STREAMING':
          {
            const cId = state.conversationId;
            const tId = state.turnId;
            resetPendingSend();
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            await cancelActiveTurn(cId, tId, reason);
            return;
          }
        case 'AWAITING_TOOL':
        case 'AWAITING_APPROVAL':
          {
            const cId = state.conversationId;
            const tId = state.turnId;
            resetPendingSend();
            dispatch({
              type: 'CANCEL_MARK'
            });
            dispatch({
              type: 'TOOL_CLEAR'
            });
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            dispatch({
              type: 'CANCEL_CLEAR'
            });
            toolResolver.cancelPendingApproval();
            await cancelActiveTurn(cId, tId, reason);
            return;
          }
        default:
          return;
      }
    },
    steerTurn,
    clear,
    setAgentId(newAgentId) {
      if (newAgentId === state.agentId) return;
      clear();
      dispatch({
        type: 'AGENT_ID_SET',
        agentId: newAgentId
      });
    },
    destroy() {
      titlePollGeneration++;
      logger.clientDestroyed();
      transport.stop();
      transport.clear();
    },
    loadConversation: id => conversationLoader.loadConversation(id),
    loadMore: () => conversationLoader.loadMore(),
    injectMessage(message) {
      if (state.status !== 'READY') return;
      dispatch({
        type: 'MESSAGE_APPEND',
        message
      });
    },
    async submitFeedback(opts) {
      var _opts$turnId, _opts$tags;
      const {
        citationId,
        removed
      } = opts;
      const targetTurnId = (_opts$turnId = opts.turnId) !== null && _opts$turnId !== void 0 ? _opts$turnId : state.turnId;
      if (state.conversationId === null || !targetTurnId) return;
      await transport.submitFeedback(Object.assign({
        conversationId: state.conversationId,
        turnId: targetTurnId,
        feedbackType: opts.type
      }, opts.text ? {
        feedbackText: opts.text
      } : {}, {
        tags: (_opts$tags = opts.tags) !== null && _opts$tags !== void 0 ? _opts$tags : []
      }, citationId ? {
        citationId
      } : {}, removed ? {
        removed
      } : {}));
      if (removed) {
        dispatch(Object.assign({
          type: 'FEEDBACK_REMOVED',
          turnId: targetTurnId
        }, citationId ? {
          citationId
        } : {}));
      } else {
        dispatch(Object.assign({
          type: 'FEEDBACK_SUBMITTED',
          turnId: targetTurnId,
          feedbackType: opts.type
        }, citationId ? {
          citationId
        } : {}));
      }
    }
  };
}
export function createClient(options) {
  var _options$mode, _options$logger, _options$conversation, _options$__conversati;
  const mode = (_options$mode = options.mode) !== null && _options$mode !== void 0 ? _options$mode : 'streaming';
  const logger = (_options$logger = options.logger) !== null && _options$logger !== void 0 ? _options$logger : createChatLogger({
    transportMode: mode
  });
  // Always-on metrics sink, kept separate from the logger and threaded through
  // the transports and coordinator (see ChatTelemetry).
  const telemetry = createChatTelemetry(mode);
  let transport;
  if (mode === 'ably') {
    var _options$__ablyConnec;
    const connection = (_options$__ablyConnec = options.__ablyConnection) !== null && _options$__ablyConnec !== void 0 ? _options$__ablyConnec : createAblyBreezeConnection({
      ablyClient: options.ablyClient,
      pollingChirpClient: options.pollingChirpClient,
      getAblyAuthToken: options.getAblyAuthToken,
      onAblyChannelEvent: options.onAblyChannelEvent,
      logger
    });
    // The client-vs-server tool predicate for the stall watchdog is installed
    // by createChatClient via transport.setClientTool (the live toolMap +
    // registry closure), so it stays in sync with runtime setTools() changes.
    transport = createAblyTransport(connection, logger, {}, telemetry);
  } else if (mode === 'polling') {
    var _options$__pollingCon;
    const connection = (_options$__pollingCon = options.__pollingConnection) !== null && _options$__pollingCon !== void 0 ? _options$__pollingCon : createPollingBreezeConnection(options.pollingChirpClient);
    transport = createPollingTransport(connection, logger, telemetry);
  } else {
    var _options$__streamingC;
    const connection = (_options$__streamingC = options.__streamingConnection) !== null && _options$__streamingC !== void 0 ? _options$__streamingC : createStreamingBreezeConnection(options.chirpClient);
    transport = createStreamingTransport(connection, logger, Object.assign({}, options.__replayMode && {
      suppressDeadChannelComplete: true
    }), telemetry);
  }
  const conversationsChirpClient = (_options$conversation = options.conversationsChirpClient) !== null && _options$conversation !== void 0 ? _options$conversation : createDefaultChirpClient();
  const conversationHistory = (_options$__conversati = options.__conversationHistory) !== null && _options$__conversati !== void 0 ? _options$__conversati : createConversationHistory(conversationsChirpClient);
  return createChatClient({
    transport,
    agentId: options.agentId,
    conversationHistory,
    conversationsChirpClient,
    pollConversationTitle: options.pollConversationTitle,
    logger,
    telemetry,
    transportMode: mode,
    tools: options.tools,
    __onInit: options.__onInit,
    onError: options.onError,
    onConversationCreated: options.onConversationCreated,
    onStatusChange: options.onStatusChange,
    onStreamEvent: options.onStreamEvent,
    onTitleChanged: options.onTitleChanged,
    onBeforeTurnComplete: options.onBeforeTurnComplete,
    onAfterTurnComplete: options.onAfterTurnComplete
  });
}